import { Button, Textarea as TTextArea } from '@heroui/react';
import { useRef, useState } from 'react';
import { IoLogoMarkdown } from 'react-icons/io5';
import { Dialog } from '../../Overlay/Modal/Dialog';
import { Markdown } from './Markdown';

interface MarkdownEditorModalProps {
  title: string;
  initialValue: string;
  onSave: (value: string) => void;
}

export const MarkdownEditorModal = ({ title, initialValue, onSave }: MarkdownEditorModalProps) => {
  const [draftValue, setDraftValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const isSyncing = useRef(false);

  const sync = (src: HTMLElement | null, dest: HTMLElement | null) => {
    if (!src || !dest || isSyncing.current) return;
    const sMax = src.scrollHeight - src.clientHeight;
    const dMax = dest.scrollHeight - dest.clientHeight;

    isSyncing.current = true;
    dest.scrollTop = (src.scrollTop / (sMax || 1)) * dMax;
    requestAnimationFrame(() => {
      isSyncing.current = false;
    });
  };
  // We use the trigger's action to populate the draft
  const handleOpen = (openFn: () => void) => {
    setDraftValue(initialValue);
    openFn();
  };

  return (
    <Dialog
      size='full'
      classNames={{
        base: 'max-w-[80vw] max-h-[90vh] rounded-md border border-default-200 bg-content1',
        body: 'p-5',
      }}
      scrollBehavior='inside'
      placement='center'
      title={title}
      trigger={(open) => (
        <button
          onClick={() => handleOpen(open)}
          type='button'
          className='flex gap-1 cursor-pointer bg-background p-1.5 border border-default-300 rounded-md items-center justify-center hover:bg-default-100 transition-colors'
        >
          <IoLogoMarkdown className='size-3' />
        </button>
      )}
      footer={(onClose) => (
        <>
          <Button variant='light' onPress={onClose}>
            Cancel
          </Button>
          <Button
            color='primary'
            onPress={() => {
              onSave(draftValue);
              onClose();
            }}
          >
            Save
          </Button>
        </>
      )}
    >
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 h-[60vh]'>
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium text-default-500'>Edit</span>
          <TTextArea
            ref={textareaRef}
            variant='faded'
            value={draftValue}
            onValueChange={setDraftValue}
            onScroll={() => sync(textareaRef.current, previewRef.current)}
            className='h-full'
            classNames={{
              inputWrapper: 'h-full',
              input: 'min-h-[calc(100vh-220px)] scrollbar-thin',
            }}
            autoFocus
          />
        </div>
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium text-default-500'>Preview</span>
          <div
            ref={previewRef}
            onScroll={() => sync(previewRef.current, textareaRef.current)}
            className='h-full max-h-[calc(100vh-200px)] overflow-auto rounded-md border border-default-200 p-3 bg-content1 scrollbar-thin'
          >
            <Markdown className='prose prose-sm max-w-none'>{draftValue || '*Nothing to preview*'}</Markdown>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
